import React, { useState, useEffect, useRef, useCallback } from 'react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import OwnerVehiclesModal from './OwnerVehiclesModal.js';
import { Users, Plus } from 'lucide-react';
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';


const OwnerVehiclesPreview = ({ vehicles, onViewAll }) => {
  if (!vehicles?.length) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  if (vehicles.length === 1) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
        {vehicles[0].vehicle_number}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onViewAll(vehicles)}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-orange-600 hover:bg-orange-50 border border-orange-200 transition"
      title={`View all ${vehicles.length} vehicles`}
    >
      View all ({vehicles.length})
    </button>
  );
};

const VehicleOwnerList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [vehiclesModal, setVehiclesModal] = useState(null);
  const dropdownRef = useRef(null);
  const perPage = 10;

  const fetchOwners = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;

      const response = await vehicleOwnerService.getAll(currentPage, perPage, filters);
      setOwners((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
      setTotalPages(response.pagination?.pages || 1);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      Alert.error('Failed to load vehicle owners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchOwners,
    setCurrentPage,
    resetPageDeps: [search],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDropdown]);

  const handleDelete = async (ownerId, ownerName) => {
    setActiveDropdown(null);

    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${ownerName}"? This action cannot be undone.`,
      'Delete Vehicle Owner'
    );
    if (!confirmed) return;

    try {
      await vehicleOwnerService.delete(ownerId);
      Alert.success('Vehicle owner deleted successfully!');
      if (owners.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchOwners();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete vehicle owner');
    }
  };

  const toggleDropdown = (ownerId) => {
    setActiveDropdown(activeDropdown === ownerId ? null : ownerId);
  };

  const clearFilters = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search;

  const openVehiclesModal = (owner) => {
    setVehiclesModal({
      vehicleOwnerId: owner.vehicle_owner_id,
      ownerName: owner.owner_name,
      vehicles: owner.vehicles_data || [],
    });
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <ListPageHeader
          title="Vehicle Owners"
          icon={Users}
          className="mb-4 sm:mb-4"
        >
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/vehicle-owners/add')}>
            Add Owner
          </Button>
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Owner name, contact..."
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>
        </ListFilterPanel>

        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading vehicle owners..." />
          </div>
        ) : owners.length === 0 ? (
          <div className="p-8 text-center text-gray-600 bg-white rounded-xl shadow-sm">
            No vehicle owners found.{' '}
            <button onClick={() => navigate('/vehicle-owners/add')} className="text-orange-500 hover:underline">
              Add one
            </button>
          </div>
        ) : (
          <>
            {/* Table - Desktop View */}
            <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
              <div className="w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[960px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 border-b">
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">OWNER NAME</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CONTACT</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap min-w-[160px]">EMAIL</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">GST</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">VEHICLES</th>
                      <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">OWNERSHIP %</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {owners.map((owner) => (
                      <tr key={owner.vehicle_owner_id} className="hover:bg-gray-50 transition-colors relative">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/vehicle-owners/view/${owner.vehicle_owner_id}`)}
                            className={listIdLinkClass}
                          >
                            {owner.owner_name}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{owner.contact || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 min-w-[160px]">
                          <span className="block truncate max-w-[200px]">{owner.email || '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{owner.gst || '—'}</td>
                        <td className="px-4 py-3">
                          <OwnerVehiclesPreview
                            vehicles={owner.vehicles_data}
                            onViewAll={() => openVehiclesModal(owner)}
                          />
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700 whitespace-nowrap">
                          {owner.owner_percentage != null ? `${owner.owner_percentage}%` : '—'}
                        </td>
                        <td className="px-4 py-3 relative whitespace-nowrap">
                          <RowActionsMenu
                            isOpen={activeDropdown === owner.vehicle_owner_id}
                            onToggle={() => toggleDropdown(owner.vehicle_owner_id)}
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => navigate(`/vehicle-owners/view/${owner.vehicle_owner_id}`)}
                            onEdit={() => navigate(`/vehicle-owners/edit/${owner.vehicle_owner_id}`)}
                            onDelete={() => handleDelete(owner.vehicle_owner_id, owner.owner_name)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-5 py-3 flex items-center justify-between border-t-2 border-gray-200">
                <span className="text-sm text-gray-600">
                  Showing{' '}
                  <span className="font-semibold">
                    {totalCount > 0
                      ? `${((currentPage - 1) * perPage) + 1}–${Math.min(currentPage * perPage, totalCount)} of ${totalCount}`
                      : owners.length}
                  </span>{' '}
                  owner{totalCount !== 1 ? 's' : ''}
                </span>
                <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading && currentPage > 1}
          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
        />
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="xl:hidden space-y-4">
              {owners.map((owner) => (
                <div key={owner.vehicle_owner_id} className="bg-white rounded-xl shadow p-4">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => navigate(`/vehicle-owners/view/${owner.vehicle_owner_id}`)}
                        className={`${listIdLinkMobileClass} truncate block text-left`}
                      >
                        {owner.owner_name}
                      </button>
                      {owner.contact && (
                        <p className="text-sm text-gray-600 mt-1">{owner.contact}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <RowActionsMenu
                        isOpen={activeDropdown === owner.vehicle_owner_id}
                        onToggle={() => toggleDropdown(owner.vehicle_owner_id)}
                        onClose={() => setActiveDropdown(null)}
                        menuRef={dropdownRef}
                        onView={() => navigate(`/vehicle-owners/view/${owner.vehicle_owner_id}`)}
                        onEdit={() => navigate(`/vehicle-owners/edit/${owner.vehicle_owner_id}`)}
                        onDelete={() => handleDelete(owner.vehicle_owner_id, owner.owner_name)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    {owner.email && (
                      <div>
                        <span className="text-gray-500 font-medium">Email:</span>
                        <p className="text-gray-900 truncate mt-0.5">{owner.email}</p>
                      </div>
                    )}
                    {owner.gst && (
                      <div>
                        <span className="text-gray-500 font-medium">GST:</span>
                        <p className="text-gray-900 mt-0.5">{owner.gst}</p>
                      </div>
                    )}
                    {owner.owner_percentage != null && (
                      <div>
                        <span className="text-gray-500 font-medium">Ownership:</span>
                        <p className="text-gray-900 mt-0.5">{owner.owner_percentage}%</p>
                      </div>
                    )}
                    <div className={owner.email && owner.gst ? '' : owner.owner_percentage != null ? '' : 'col-span-2'}>
                      <span className="text-gray-500 font-medium">Vehicles:</span>
                      <div className="mt-0.5">
                        <OwnerVehiclesPreview
                          vehicles={owner.vehicles_data}
                          onViewAll={() => openVehiclesModal(owner)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              
            </div>
          </>
        )}

        <OwnerVehiclesModal
          open={!!vehiclesModal}
          onClose={() => setVehiclesModal(null)}
          vehicleOwnerId={vehiclesModal?.vehicleOwnerId}
          ownerName={vehiclesModal?.ownerName}
          vehicles={vehiclesModal?.vehicles}
        />
      </div>
    </DashboardLayout>
  );
};

export default VehicleOwnerList;
